import { memo } from 'react'
import { Check, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ArrivalInquiry, Triage } from '../engine/types'
import { formatShort } from '../data/formatTime'

const TRIAGE_DOT: Record<Triage, string> = {
  green: 'bg-emerald-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
}

interface Props {
  inquiry: ArrivalInquiry
  placed: boolean
  confirmed: boolean
  selected: boolean
  onConfirm: () => void
  onSelect: () => void
}

function ArrivalInquiryRowImpl({ inquiry, placed, confirmed, selected, onConfirm, onSelect }: Props) {
  return (
    <div
      onClick={placed ? onSelect : undefined}
      className={cn(
        'flex items-center gap-2 border-b border-white/5 px-3 py-2 text-xs',
        placed ? 'cursor-pointer bg-white/[0.02] hover:bg-white/[0.06]' : 'bg-transparent',
        selected && 'bg-sky-500/15 ring-1 ring-inset ring-sky-400/40'
      )}
    >
      <span className={cn('h-2 w-2 shrink-0 rounded-full', TRIAGE_DOT[inquiry.triage])} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-white">{inquiry.tailNumber}</span>
          <span className="text-white/40">{inquiry.typeCode}</span>
          {inquiry.isVip && <Crown className="h-3 w-3 text-amber-300" />}
        </div>
        <div className="truncate text-[10px] text-white/40">{inquiry.operatorName}</div>
        <div className="text-[10px] text-white/30">
          {formatShort(inquiry.arrival)} → {formatShort(inquiry.departure)}
        </div>
        {inquiry.services.length > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            {inquiry.services.map((s, i) => (
              <span
                key={i}
                className="rounded bg-sky-500/15 px-1 text-[9px] uppercase tracking-wide text-sky-300"
              >
                {String(s.type)}
                {s.type === 'fuel' && s.gallons ? ` ${s.gallons}g` : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      {placed ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onConfirm()
          }}
          disabled={confirmed}
          className={cn(
            'flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-[10px] font-medium',
            confirmed
              ? 'bg-emerald-500/20 text-emerald-300'
              : 'bg-white/10 text-white/70 hover:bg-white/20'
          )}
          title={confirmed ? 'Confirmed' : 'Confirm placement'}
        >
          <Check className="h-3 w-3" />
          {confirmed ? 'Confirmed' : 'Confirm'}
        </button>
      ) : (
        <span className="shrink-0 text-[10px] text-white/25">queued</span>
      )}
    </div>
  )
}

export const ArrivalInquiryRow = memo(ArrivalInquiryRowImpl)
